import classNames from 'classnames';
import React, { type AriaAttributes, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import Select from '../Select/Select';

import styles from './Filter.module.scss';

type FilterOption =
  | {
      label: string;
      value: string;
    }
  | string;

type Props = {
  className?: string;
  name: string;
  value: string;
  defaultLabel: string;
  options: FilterOption[];
  setValue: (value: string) => void;
  type?: 'buttons' | 'dropdown';
};

const FilterButton = ({ label, onClick, active, ...rest }: { label: string; onClick: () => void; active: boolean; role?: string } & AriaAttributes) => {
  return (
    <button className={classNames(styles.button, active && styles.active)} onClick={onClick} {...rest}>
      {label}
    </button>
  );
};

const Filter: FC<Props> = ({ className, name, value, defaultLabel, options, setValue, type = 'buttons' }) => {
  const { t } = useTranslation('common');

  if (!options.length) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => setValue(event.target.value);

  if (type === 'dropdown') {
    return (
      <div className={styles.filterDropDown}>
        <Select
          className={styles.dropDown}
          size="small"
          options={options}
          defaultLabel={defaultLabel}
          name={name}
          value={value}
          onChange={handleChange}
          aria-label={t('filter_videos_by', { name })}
        />
      </div>
    );
  }

  return (
    <div className={classNames(styles.filterRow, className)} role="listbox" aria-label={t('filter_videos_by', { name })}>
      <FilterButton label={defaultLabel} onClick={() => setValue('')} active={value === ''} key={defaultLabel} role="option" aria-selected={value === ''} />
      {options.map((option) => {
        const optionLabel = typeof option === 'string' ? option : option.label;
        const optionValue = typeof option === 'string' ? option : option.value;
        const active = value === optionValue;

        return (
          <FilterButton label={optionLabel} onClick={() => setValue(optionValue)} key={optionValue} active={active} role="option" aria-selected={active} />
        );
      })}
    </div>
  );
};

export default Filter;
